import type { MouseEvent } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Chat from '@mui/icons-material/Chat';
import FilterList from '@mui/icons-material/FilterList';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import type { User } from '~/shared/types/connect';
import {
  ExpertChip,
  ExpertChipAvatar,
  ExpertChipName,
  ExpertChipStatus,
} from '~/modules/matters/MatterCard.styles';
import { getRetainerStatusDisplay } from '~/modules/matters/matterCardUtils';
import DropdownFilter from '~/modules/matters/DropdownFilter';
import { ExpertsEmptyCalloutPaper } from '~/modules/matters/detail/MatterDetail.styles';
import type { ExpertsOnMatterResult } from '~/modules/matters/useMatterDetail';
import {
  EXPERT_STATUS_FILTER_OPTIONS,
  ExpertStatus,
  getExpertStatusLabel,
} from '~/modules/matters/detail/expertsTabUtils';

interface Props {
  hasAnyExperts: boolean;
  expertStatusFilter: ExpertStatus;
  setExpertStatusFilter: (value: ExpertStatus) => void;
  filteredHiredExperts: User[];
  expertsOnMatter: ExpertsOnMatterResult;
  isMatterClosed: boolean;
  onOpenChat: (expertId: string) => void;
  onRemoveExpert: (expertId: string, isInvited: boolean) => void;
  getUnreadCountForExpert: (expertId: string) => number;
}

const ExpertsEmptyCallout = () => (
  <ExpertsEmptyCalloutPaper variant="outlined">
    <Typography variant="body2" color="text.secondary">
      No experts on this matter yet. Find experts or add them to get started.
    </Typography>
  </ExpertsEmptyCalloutPaper>
);

interface ExpertRowProps {
  expert: User;
  statusLabel?: string | null;
  statusColor?: 'red' | 'warning' | 'success' | 'default';
  isInvited: boolean;
  onRemove: (id: string, isInvited: boolean) => void;
  onOpenChat: (id: string) => void;
  unreadFromExpert?: number;
}

const ExpertRow = ({
  expert,
  statusLabel,
  statusColor = 'default',
  isInvited,
  onRemove,
  onOpenChat,
  unreadFromExpert = 0,
}: ExpertRowProps) => {
  const messageButton = (
    <MessageButton
      variant="contained"
      size="small"
      startIcon={<Chat sx={{ fontSize: 16, width: 16, height: 16 }} />}
      onClick={() => onOpenChat(expert.id)}
    >
      Message
    </MessageButton>
  );
  const removeButton = (
    <IconButton
      aria-label="Remove expert"
      onClick={(e) => {
        e.preventDefault();
        onRemove(expert.id, isInvited);
      }}
      size="small"
      sx={{
        color: 'red.main',
        '&:hover': { color: 'red.dark' },
      }}
    >
      <RemoveCircleOutline sx={{ fontSize: 18 }} />
    </IconButton>
  );

  return (
    <ExpertRowStack>
      <ExpertChipAndRemoveRow>
        <ExpertChip
          to={`/experts/${expert.id}`}
          onClick={(event: MouseEvent) => event.stopPropagation()}
          aria-label={`View ${expert.name ?? 'Expert'}'s profile`}
        >
          <ExpertChipAvatar
            avatarUrl={expert.avatarUrl}
            initialsLabel={expert.name ?? 'Expert'}
            alt={expert.name ?? ''}
          />
          <ExpertChipName variant="caption">
            {expert.name ?? 'Expert'}
          </ExpertChipName>
          {statusLabel !== null && statusLabel !== '' ? (
            <ExpertChipStatus
              variant="caption"
              component="span"
              $color={statusColor}
            >
              {statusLabel}
            </ExpertChipStatus>
          ) : null}
        </ExpertChip>
        <RemoveButtonMobile>{removeButton}</RemoveButtonMobile>
      </ExpertChipAndRemoveRow>
      <ExpertRowActions>
        {unreadFromExpert > 0 ? (
          <UnreadBadge badgeContent={unreadFromExpert} color="error">
            {messageButton}
          </UnreadBadge>
        ) : (
          messageButton
        )}
        <RemoveButtonDesktop>{removeButton}</RemoveButtonDesktop>
      </ExpertRowActions>
    </ExpertRowStack>
  );
};

export const ExpertsTabContent = ({
  hasAnyExperts,
  expertStatusFilter,
  setExpertStatusFilter,
  filteredHiredExperts,
  expertsOnMatter,
  isMatterClosed,
  onOpenChat,
  onRemoveExpert,
  getUnreadCountForExpert,
}: Props) => {
  const expertStatusLabel = getExpertStatusLabel(expertStatusFilter);
  const showAddedExperts =
    expertsOnMatter.addedExperts.length > 0 &&
    (expertStatusFilter === ExpertStatus.All ||
      expertStatusFilter === ExpertStatus.Pending);
  const totalDisplayed =
    filteredHiredExperts.length +
    (showAddedExperts ? expertsOnMatter.addedExperts.length : 0);
  return (
    <Box sx={{ pt: 0.5 }}>
      {!hasAnyExperts ? (
        <ExpertsEmptyCallout />
      ) : (
        <>
          <ExpertFilterRow>
            <DropdownFilter
              ariaLabel="Filter by status"
              labelPrefix="Status:"
              valueLabel={expertStatusLabel}
              icon={<FilterList fontSize="small" color="action" aria-hidden />}
              options={EXPERT_STATUS_FILTER_OPTIONS}
              onSelect={(value) => setExpertStatusFilter(value)}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 'auto', fontWeight: 600 }}
            >
              Total: {totalDisplayed}
            </Typography>
          </ExpertFilterRow>
          {filteredHiredExperts.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <ExpertListStack divider={<Divider />}>
                {filteredHiredExperts.map((hiredExpert) => {
                  const statusEntry =
                    expertsOnMatter.expertRetainerStatuses.find(
                      (entry) => entry.expertId === hiredExpert.id,
                    );
                  const statusDisplay = isMatterClosed
                    ? null
                    : getRetainerStatusDisplay(statusEntry?.status ?? null);
                  return (
                    <ExpertRow
                      key={hiredExpert.id}
                      expert={hiredExpert}
                      statusLabel={statusDisplay?.label}
                      statusColor={statusDisplay?.color ?? 'default'}
                      isInvited={false}
                      onRemove={onRemoveExpert}
                      onOpenChat={onOpenChat}
                      unreadFromExpert={getUnreadCountForExpert(hiredExpert.id)}
                    />
                  );
                })}
              </ExpertListStack>
            </Box>
          ) : null}
          {showAddedExperts ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Pending Retainer Request
              </Typography>
              <ExpertListStack divider={<Divider />}>
                {expertsOnMatter.addedExperts.map((addedExpert) => (
                  <ExpertRow
                    key={addedExpert.id}
                    expert={addedExpert}
                    statusLabel="No retainer yet"
                    statusColor="default"
                    isInvited
                    onRemove={onRemoveExpert}
                    onOpenChat={onOpenChat}
                    unreadFromExpert={getUnreadCountForExpert(addedExpert.id)}
                  />
                ))}
              </ExpertListStack>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

const ExpertFilterRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'nowrap',
    minWidth: 0,
    '& > *:first-of-type': { minWidth: 0, flexShrink: 1 },
  },
}));

const MessageButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: theme.typography.body2.fontSize,
  backgroundColor: theme.palette.amount?.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.amount?.dark,
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

const UnreadBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    minWidth: 18,
    height: 18,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    transform: 'scale(1) translate(50%, -50%) translate(-4px, 4px)',
    backgroundColor:
      (theme.palette as { red?: { main: string } }).red?.main ?? '#C62828',
    color: theme.palette.common.white,
  },
}));

const ExpertRowStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'center',
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
}));

const ExpertChipAndRemoveRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    flex: 'none',
  },
}));

const RemoveButtonMobile = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
  },
}));

const RemoveButtonDesktop = styled(Box)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const ExpertRowActions = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center',
  marginLeft: 'auto',
  '& .MuiButton-root': {
    width: 130,
    minWidth: 130,
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    '& .MuiButton-root': {
      width: '100%',
      minWidth: 'unset',
    },
  },
}));

const ExpertListStack = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
}));
